import { useEffect, useState } from 'react'
import { Button, DatePicker, Dialog, EmptyNote, Field, IconButton, Icons, Loading, StateNote, StatusLabel, Truncate } from '@workflow/ui'
import { navigate } from '@/app/router'
import { describeError } from '@/lib/api/client'
import { useArchivedPaymentSheets, useCreatePaymentSheet, usePaymentSheets, useRestorePaymentSheet } from '@/lib/api/hooks'
import { PageFrame } from '@/shell/PageFrame'
import { PaymentSheetList, PaymentSheetListRow } from './PaymentSheetList'
import { hoursText, periodLabel } from './sheetMath'

const pad = (value: number) => String(value).padStart(2, '0')
const thisMonth = () => {
  const date = new Date()
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}
const monthRange = (month: string): [string, string] => {
  const [year, value] = month.split('-').map(Number) as [number, number]
  return [`${month}-01`, `${month}-${pad(new Date(year, value, 0).getDate())}`]
}

/** Payments: the studio's monthly sheets, newest first, and where to start the next one. */
export function PaymentSheetsPage() {
  const sheets = usePaymentSheets()
  const archived = useArchivedPaymentSheets()
  const restore = useRestorePaymentSheet()
  const [showArchived, setShowArchived] = useState(false)
  const [making, setMaking] = useState(false)
  return (
    <PageFrame
      title="Payments"
      description="One sheet per month, built from the hours players log. Mark each player paid as the money goes out."
      actions={<>
        <IconButton label={showArchived ? 'Active payment sheets' : 'Archived payment sheets'} size="sm" active={showArchived} onClick={() => setShowArchived((value) => !value)}><Icons.archive /></IconButton>
        <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setMaking(true)}>New sheet</Button>
      </>}
    >
      <h2 className="mb-3 text-micro text-muted-foreground uppercase">{showArchived ? 'Archived sheets' : 'Payment sheets'}</h2>
      {showArchived ? archived.isLoading ? (
        <Loading label="Loading archived payment sheets" />
      ) : archived.error || !archived.data ? (
        <StateNote title="The archived sheets did not load." detail={describeError(archived.error)} />
      ) : archived.data.length === 0 ? (
        <EmptyNote title="No archived payment sheets." detail="Sheets you archive will be listed here, where you can restore them." />
      ) : (
        <ul className="overflow-hidden rounded-2xl border border-hairline bg-surface">
          {archived.data.map((sheet) => (
            <li key={sheet.id} className="flex flex-col items-start gap-3 border-b border-hairline px-4 py-3.5 last:border-0 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
              <span className="w-full min-w-0 flex-1">
                <Truncate text={sheet.title} className="text-ui font-medium text-foreground" />
                <span className="mt-0.5 block text-caption text-muted-foreground">
                  {periodLabel(sheet.periodStart, sheet.periodEnd)} · {sheet.players} {sheet.players === 1 ? 'player' : 'players'} · {hoursText(sheet.hours)}h
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <StatusLabel tone="idle" label="Archived" />
                <span className="text-caption text-muted-foreground tabular-nums">{sheet.paid} of {sheet.players} paid</span>
                {sheet.disputed > 0 ? <StatusLabel tone="attention" label={`${sheet.disputed} disputed`} /> : null}
                <Button size="sm" variant="ghost" busy={restore.isPending} disabled={restore.isPending} onClick={() => restore.mutate(sheet.id)}>Restore</Button>
              </span>
            </li>
          ))}
        </ul>
      ) : sheets.isLoading ? (
        <Loading label="Loading payment sheets" />
      ) : sheets.error || !sheets.data ? (
        <StateNote title="The sheets did not load." detail={describeError(sheets.error)} />
      ) : sheets.data.length === 0 ? (
        <EmptyNote title="No payment sheets yet." detail="Start one for a month; every player's unpaid hours up to its last day are gathered into it." />
      ) : (
        <PaymentSheetList>
          {sheets.data.map((sheet) => (
            <PaymentSheetListRow
              key={sheet.id}
              title={sheet.title}
              detail={`${periodLabel(sheet.periodStart, sheet.periodEnd)} · ${sheet.players} ${sheet.players === 1 ? 'player' : 'players'} · ${hoursText(sheet.hours)}h`}
              onOpen={() => navigate({ name: 'payment-sheet', id: sheet.id })}
            >
              {sheet.disputed > 0 ? <StatusLabel tone="attention" label={`${sheet.disputed} disputed`} /> : null}
              <span className="text-caption text-muted-foreground tabular-nums">{sheet.paid} of {sheet.players} paid</span>
              <StatusLabel tone={sheet.sharedAt ? 'done' : 'waiting'} label={sheet.sharedAt ? 'Shared' : 'Not shared'} />
            </PaymentSheetListRow>
          ))}
        </PaymentSheetList>
      )}
      <NewSheet open={making} onClose={() => setMaking(false)} />
    </PageFrame>
  )
}

/** A month by default; any two dates when the studio pays on another rhythm. */
function NewSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [month, setMonth] = useState(thisMonth)
  const [custom, setCustom] = useState(false)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const create = useCreatePaymentSheet((id) => {
    onClose()
    navigate({ name: 'payment-sheet', id })
  })
  useEffect(() => {
    if (!open) return
    setMonth(thisMonth())
    setCustom(false)
    create.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    const [first, last] = monthRange(month)
    setStart(first)
    setEnd(last)
  }, [month])
  const [periodStart, periodEnd] = custom ? [start, end] : monthRange(month)
  const ready = Boolean(periodStart && periodEnd && periodStart <= periodEnd)

  return (
    <Dialog open={open} onClose={onClose} labelledBy="new-sheet-label" className="sm:max-w-md">
      <form
        className="rounded-3xl border bg-surface p-6 shadow-modal"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) create.mutate({ periodStart, periodEnd })
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <p id="new-sheet-label" className="text-ui font-medium text-foreground">New payment sheet</p>
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
        </div>
        {custom ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="From" htmlFor="sheet-start"><DatePicker id="sheet-start" value={start} onChange={setStart} /></Field>
            <Field label="To" htmlFor="sheet-end"><DatePicker id="sheet-end" value={end} onChange={setEnd} invalid={Boolean(start && end && start > end)} /></Field>
          </div>
        ) : (
          <Field label="Month" htmlFor="sheet-month"><DatePicker id="sheet-month" mode="month" value={month} onChange={setMonth} /></Field>
        )}
        <button type="button" onClick={() => setCustom((value) => !value)} className="mt-2 text-caption text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
          {custom ? 'Use a whole month' : 'Use other dates'}
        </button>
        <p className="mt-4 text-caption text-muted-foreground">
          Every player's unpaid hours logged up to the last day are gathered into it. Hours already on another sheet stay there.
        </p>
        {create.error ? <p className="mt-3 text-caption text-danger">{describeError(create.error)}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" busy={create.isPending} disabled={!ready}>Create sheet</Button>
        </div>
      </form>
    </Dialog>
  )
}
